/**
 * Classifies a link inside an agent reply.
 *
 * The agent writes plain markdown links to real bestjobs pages. Job and CV
 * detail links are opened as in-place modals — the same UX the result cards
 * give — by flipping the query params `ChatJobModal` and `ChatTalentModal`
 * already watch. Every other link opens in a new tab, so the chat thread is
 * never navigated away from.
 */

export type ChatLinkTarget =
  | { kind: 'job'; slug: string }
  | { kind: 'cv'; slug: string }
  | { kind: 'newTab'; href: string }
  | { kind: 'plain' };

// Romanian is the default locale and is served unprefixed; English is `/en`.
// Those are the only two languages in `src/services/i18nPaths/paths.ts`.
const LOCALE_SEGMENT = '(?:/en)?';
// Job detail is `/job/:jobSlug` in the path map -> ro `/loc-de-munca/:slug`, en `/job/:slug`.
// The required `/` after the segment is what keeps the *listing* paths out of this
// branch (`/jobs`, `/jobs-in-bacau`, `/locuri-de-munca`, `/locuri-de-munca-in-bacau`).
// These match only a bare detail path, never a sub-route beneath one (`/job/:slug/cv/:other`):
// `[^/?#]+` already stops the capture at the next `/`, and the trailing group requires what
// follows it to be the end of the path, a lone trailing slash with nothing after it, or a
// query/fragment delimiter — a `/` followed by more path (a sub-route) matches none of those.
const JOB_DETAIL = new RegExp(`^${LOCALE_SEGMENT}/(?:loc-de-munca|job)/([^/?#]+)(?:$|/$|[?#])`);
// CV detail is `/cv/:slug` in both locales (`pages/[locale]/cv/[slug].tsx`).
const CV_DETAIL = new RegExp(`^${LOCALE_SEGMENT}/cv/([^/?#]+)(?:$|/$|[?#])`);

// Sibling routes that sit where a slug would: `/job/list` (the untranslated
// internal listing route) and `/cv/search`. Without this, the see-more links
// would try to open a modal for a job called "list" or a candidate called
// "search".
const RESERVED_SLUGS = new Set(['list', 'search']);

const isBestjobsHost = (host: string): boolean => host === 'bestjobs.eu' || host.endsWith('.bestjobs.eu');

export const chatLinkTarget = (href: string | undefined): ChatLinkTarget => {
  if (!href) return { kind: 'plain' };

  let url: URL;
  try {
    // Parsed for classification only — the base is never rendered. A relative href
    // resolves against the current page; an absolute one keeps its own host.
    url = new URL(href, typeof window === 'undefined' ? 'https://bestjobs.eu' : window.location.origin);
  } catch {
    return { kind: 'newTab', href };
  }

  // The link keeps the author's original href. Serializing `url` instead would
  // rewrite relative links against a base that differs between the server
  // (`bestjobs.eu`) and the browser (localhost/staging/prod) — a hydration mismatch.
  const newTab: ChatLinkTarget = { kind: 'newTab', href };

  // Only our own pages have modals to open. `sameSite` is what makes this work on
  // the local `*.bestjobs.dev` and staging hosts too.
  const sameSite = typeof window !== 'undefined' && url.host === window.location.host;
  if (!sameSite && !isBestjobsHost(url.host)) return newTab;

  const job = JOB_DETAIL.exec(url.pathname);
  if (job && !RESERVED_SLUGS.has(job[1])) return { kind: 'job', slug: job[1] };

  const cv = CV_DETAIL.exec(url.pathname);
  if (cv && !RESERVED_SLUGS.has(cv[1])) return { kind: 'cv', slug: cv[1] };

  return newTab;
};
